import { TextField, Box, CircularProgress, Tooltip, useTheme } from '@mui/material';
import { OrderItemDetails } from '../../../app/services/api/orderManagementClient';
import React from 'react';
import { useTranslation } from 'react-i18next';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';

export type SaveStatus = 'standard' | 'idle' | 'saving' | 'saved' | 'error';

type QuantityCellProps = {
	item: OrderItemDetails;
	initialQuantity: number;
	canChange: boolean;
	isQuantityError: (item: OrderItemDetails) => boolean;
	quantityError: (item: OrderItemDetails) => string | null;
	setQuantityInRef: (itemId: number, value: number) => void;
	autosaveItem: (setSaveStatus: (status: SaveStatus) => void) => Promise<void>;
	handleConfirmDeletion: (itemId: number, itemNumber: number) => Promise<void>;
	max?: number;
};

export const QuantityCell: React.FC<QuantityCellProps> = ({
  item,
  initialQuantity,
  canChange,
  isQuantityError,
  quantityError,
  setQuantityInRef,
  autosaveItem,
  handleConfirmDeletion,
  max,
}) => {
  const { t } = useTranslation('orderDetails');
  const theme = useTheme();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [value, setValue] = React.useState<string>(String(initialQuantity));
  const [saveStatus, setSaveStatus] = React.useState<SaveStatus>('standard');
  const [clampMessage, setClampMessage] = React.useState<string | null>(null);
  const clampTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const saveStartTimeRef = React.useRef<number | null>(null);
  const saveDelayTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Refs kept current every render so the unmount cleanup always sees the latest values
  // without needing them in the dependency array (which would fire cleanup on every keystroke).
  const latestValueRef = React.useRef(value);
  latestValueRef.current = value;
  const latestInitialQuantityRef = React.useRef(initialQuantity);
  latestInitialQuantityRef.current = initialQuantity;

  React.useEffect(() => {
    setValue(String(initialQuantity));
    if (saveStatus !== 'saving') {
      setSaveStatus('standard');
    }
  }, [initialQuantity]);

  const showClampFeedback = (maxVal: number) => {
    if (clampTimerRef.current) clearTimeout(clampTimerRef.current);
    setClampMessage(t('maxPromoQuantity', { max: maxVal }));
    clampTimerRef.current = setTimeout(() => setClampMessage(null), 3000);
  };

  const handleQuantityChange = (newValue: string) => {
    const parsed = parseInt(newValue, 10);
    if (max != null && !isNaN(parsed) && parsed > max) {
      setValue(String(max));
      showClampFeedback(max);
    } else {
      setValue(newValue);
      setClampMessage(null);
    }
    const display = max != null && !isNaN(parsed) && parsed > max ? String(max) : newValue;
    if (display !== String(initialQuantity)) {
      setSaveStatus('idle');
    } else {
      setSaveStatus('standard');
    }
  };

  const handleQuantityBlur = React.useCallback(async (passedValue: string) => {
    let value = Math.abs(parseInt(passedValue || '0', 10));
    if (isNaN(value) || value < 0) value = 0;
    if (max != null && value > max) {
      showClampFeedback(max);
      value = max;
    }
    setValue(String(value));
    setQuantityInRef(item.id, value);

    if (value === 0) {
      await handleConfirmDeletion(item.id, item.itemId);
    } else {
      // Wrapper function to ensure "saving" is visible for at least 500ms
      const setSaveStatusWithMinDuration = (status: SaveStatus) => {
        if (status === 'saving') {
          saveStartTimeRef.current = Date.now();
          setSaveStatus('saving');
        } else {
          // When save completes, ensure it was visible for at least 500ms
          const elapsed = saveStartTimeRef.current ? Date.now() - saveStartTimeRef.current : 0;
          const remainingTime = Math.max(0, 500 - elapsed);
                    
          if (saveDelayTimerRef.current) {
            clearTimeout(saveDelayTimerRef.current);
          }
                    
          if (remainingTime > 0) {
            // Wait for remaining time before showing final status
            saveDelayTimerRef.current = setTimeout(() => {
              setSaveStatus(status);
              saveStartTimeRef.current = null;
            }, remainingTime);
          } else {
            // 500ms already elapsed, show final status immediately
            setSaveStatus(status);
            saveStartTimeRef.current = null;
          }
        }
      };
      await autosaveItem(setSaveStatusWithMinDuration);
    }
  }, [autosaveItem, handleConfirmDeletion, item.id, item.itemId, setQuantityInRef, max]);

  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleQuantityChange(e.target.value);
  }, [initialQuantity]);

  const handleBlur = React.useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    void handleQuantityBlur(e.target.value);
  }, [handleQuantityBlur]);

  const handleFocus = React.useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  }, []);

  const latestHandleQuantityBlurRef = React.useRef(handleQuantityBlur);
  React.useEffect(() => {
    latestHandleQuantityBlurRef.current = handleQuantityBlur;
  }, [handleQuantityBlur]);

  React.useEffect(() => {
    return () => {
      if (saveDelayTimerRef.current) {
        clearTimeout(saveDelayTimerRef.current);
      }
      if (latestValueRef.current !== String(latestInitialQuantityRef.current)) {
        void latestHandleQuantityBlurRef.current(latestValueRef.current);
      }
    };
  }, []); // empty — cleanup only runs on unmount, refs supply latest values

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <TextField
        size="small"
        type="number"
        autoComplete="off"
        disabled={!canChange || saveStatus === 'saving'}
        inputProps={{ min: 0 }}
        value={value}
        error={isQuantityError(item)}
        helperText={quantityError(item)}
        inputRef={inputRef}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        sx={{
          width: '100px',
          input: { textAlign: 'center' },
        }}
      />
      {/* Status indicators */}
      {saveStatus === 'idle' && (
        <Box sx={{ position: 'absolute', top: -6, right: -6, color: theme.palette.warning.main }}>
          <WarningIcon sx={{ fontSize: 18 }} />
        </Box>
      )}
      {saveStatus === 'saving' && (
        <Box sx={{ position: 'absolute', top: -4, right: -4 }}>
          <CircularProgress size={16} />
        </Box>
      )}
      {saveStatus === 'saved' && (
        <Box sx={{ position: 'absolute', top: -4, right: -4, color: theme.palette.success.main }}>
          <CloudDoneIcon sx={{ fontSize: 16 }} />
        </Box>
      )}
      {saveStatus === 'error' && (
        <Box sx={{ position: 'absolute', top: -4, right: -4, color: theme.palette.error.main }}>
          <ErrorIcon sx={{ fontSize: 16 }} />
        </Box>
      )}
      {clampMessage && saveStatus !== 'idle' && saveStatus !== 'saving' && (
        <Tooltip title={clampMessage}
          arrow>
          <Box sx={{ position: 'absolute', top: -6, right: -6, color: theme.palette.warning.main, cursor: 'pointer' }}>
            <WarningIcon sx={{ fontSize: 18 }} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};